import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { api } from './utils/api';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import OwnerDashboard from './pages/OwnerDashboard';
import Monitoring from './pages/Monitoring';
import AdminDashboard from './pages/AdminDashboard';
import PaymentRequests from './pages/PaymentRequests';
import PaymentRequisites from './pages/PaymentRequisites';
import Buyers from './pages/Buyers';
import AuditLogs from './pages/AuditLogs';
import ShopSettings from './components/ShopSettings';
import Operators from './pages/Operators';
import Payments from './pages/Payments';
import Withdrawals from './pages/Withdrawals';
import Operations from './pages/Operations';
import Reports from './pages/Reports';
import Layout from './components/Layout';

const PrivateRoute: React.FC<{ children: React.ReactNode; allowedRoles?: string[] }> = ({
  children,
  allowedRoles,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Загрузка...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role === 'owner') {
    return <Navigate to="/owner" />;
  }

  if (user.role === 'admin') {
    return <Navigate to="/admin" />;
  }

  return <Navigate to="/dashboard" />;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <GlobalPopupNotifier />
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route
            element={
              <PrivateRoute allowedRoles={['admin', 'operator']}>
                <Layout />
              </PrivateRoute>
            }
          >
            <Route
              path="/admin"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/dashboard"
              element={
                <PrivateRoute allowedRoles={['operator']}>
                  <Dashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/payments"
              element={
                <PrivateRoute allowedRoles={['admin', 'operator']}>
                  <Payments />
                </PrivateRoute>
              }
            />

            <Route
              path="/withdrawals"
              element={
                <PrivateRoute allowedRoles={['admin', 'operator']}>
                  <Withdrawals />
                </PrivateRoute>
              }
            />

            <Route
              path="/operations"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <Operations />
                </PrivateRoute>
              }
            />

            <Route
              path="/reports"
              element={
                <PrivateRoute allowedRoles={['admin', 'operator']}>
                  <Reports />
                </PrivateRoute>
              }
            />

            <Route
              path="/operators"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <Operators />
                </PrivateRoute>
              }
            />

            <Route
              path="/requisites"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <PaymentRequisites />
                </PrivateRoute>
              }
            />

            <Route
              path="/shop-settings"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <ShopSettings />
                </PrivateRoute>
              }
            />

            <Route
              path="/payment-requests"
              element={
                <PrivateRoute allowedRoles={['operator']}>
                  <PaymentRequests />
                </PrivateRoute>
              }
            />

            <Route
              path="/payment-requisites"
              element={
                <PrivateRoute allowedRoles={['operator']}>
                  <PaymentRequisites />
                </PrivateRoute>
              }
            />

            <Route
              path="/buyers"
              element={
                <PrivateRoute allowedRoles={['admin', 'operator']}>
                  <Buyers />
                </PrivateRoute>
              }
            />

            <Route
              path="/audit-logs"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <AuditLogs />
                </PrivateRoute>
              }
            />
          </Route>

          <Route
            path="/owner"
            element={
              <PrivateRoute allowedRoles={['owner']}>
                <OwnerDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/monitoring"
            element={
              <PrivateRoute allowedRoles={['owner']}>
                <Monitoring />
              </PrivateRoute>
            }
          />

          <Route path="/" element={<AppRoutes />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

const GlobalPopupNotifier: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [msg, setMsg] = React.useState<{ text: string; to: string } | null>(null);
  const prevPayments = React.useRef<number | null>(null);
  const prevWithdrawals = React.useRef<number | null>(null);
  const playedRef = React.useRef<number>(0);

  const PAYMENT_SEEN_KEY = React.useMemo(() => `pm:last-payment-id:${user?.id ?? 'anon'}`, [user?.id]);
  const WITHDRAWAL_SEEN_KEY = React.useMemo(() => `pm:last-withdrawal-id:${user?.id ?? 'anon'}`, [user?.id]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const storedPayment = Number(window.localStorage.getItem(PAYMENT_SEEN_KEY));
    const storedWithdrawal = Number(window.localStorage.getItem(WITHDRAWAL_SEEN_KEY));
    if (Number.isFinite(storedPayment)) {
      prevPayments.current = storedPayment;
    } else {
      prevPayments.current = null;
    }
    if (Number.isFinite(storedWithdrawal)) {
      prevWithdrawals.current = storedWithdrawal;
    } else {
      prevWithdrawals.current = null;
    }
  }, [PAYMENT_SEEN_KEY, WITHDRAWAL_SEEN_KEY]);

  const updateSeenValue = React.useCallback((key: string, value: number) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, String(value));
  }, []);

  const extractMaxId = React.useCallback((items: any[]): number => {
    if (!Array.isArray(items) || !items.length) return 0;
    return items.reduce((acc, item) => {
      const id = typeof item?.id === 'number' ? item.id : Number(item?.id);
      if (!Number.isFinite(id)) return acc;
      return id > acc ? id : acc;
    }, 0);
  }, []);

  const fetchLatest = React.useCallback(
    async (scope: 'payments' | 'withdrawals' | 'both' = 'both') => {
      if (!user) return;

      const shouldFetchPayments = scope === 'payments' || scope === 'both';
      const shouldFetchWithdrawals = scope === 'withdrawals' || scope === 'both';

      const paymentsEndpoint = user.role === 'operator' ? '/operator/payments' : '/admin/payments';
      const withdrawalsEndpoint = user.role === 'operator' ? '/operator/withdrawals' : '/admin/withdrawals';

      if (shouldFetchPayments) {
        try {
          const { data } = await api.get(paymentsEndpoint);
          const maxId = extractMaxId(Array.isArray(data) ? data : []);
          if (prevPayments.current !== null && maxId > (prevPayments.current || 0)) {
            setMsg({ text: 'Новый платеж', to: '/payments' });
          }
          prevPayments.current = maxId;
          updateSeenValue(PAYMENT_SEEN_KEY, maxId);
        } catch {
          if (prevPayments.current === null) {
            prevPayments.current = 0;
          }
        }
      }

      if (shouldFetchWithdrawals) {
        try {
          const { data } = await api.get(withdrawalsEndpoint);
          const maxId = extractMaxId(Array.isArray(data) ? data : []);
          if (prevWithdrawals.current !== null && maxId > (prevWithdrawals.current || 0)) {
            setMsg({ text: 'Новая заявка на вывод', to: '/withdrawals' });
          }
          prevWithdrawals.current = maxId;
          updateSeenValue(WITHDRAWAL_SEEN_KEY, maxId);
        } catch {
          if (prevWithdrawals.current === null) {
            prevWithdrawals.current = 0;
          }
        }
      }
    },
    [user, extractMaxId, updateSeenValue, PAYMENT_SEEN_KEY, WITHDRAWAL_SEEN_KEY]
  );

  React.useEffect(() => {
    if (!user) return;
    const onPay = () => {
      void fetchLatest('payments');
    };
    const onWdr = () => {
      void fetchLatest('withdrawals');
    };
    window.addEventListener('notify:new_payment', onPay as any);
    window.addEventListener('notify:new_withdrawal', onWdr as any);
    return () => {
      window.removeEventListener('notify:new_payment', onPay as any);
      window.removeEventListener('notify:new_withdrawal', onWdr as any);
    };
  }, [user, fetchLatest]);

  React.useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const run = async () => {
      if (cancelled) return;
      await fetchLatest('both');
    };

    void run();
    const id = setInterval(run, 10000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user, fetchLatest]);

  React.useEffect(() => {
    if (!msg) return;
    const now = Date.now();
    if (now - playedRef.current < 500) return;
    playedRef.current = now;
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const t0 = ctx.currentTime;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.15, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.45);
      osc.start(t0);
      osc.stop(t0 + 0.5);
      setTimeout(() => {
        try {
          ctx.close();
        } catch {}
      }, 700);
    } catch {}
  }, [msg]);

  if (!msg) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-[18rem]">
      <button
        onClick={() => {
          const role = user?.role;
          setMsg(null);
          // Всегда открываем конкретную страницу, чтобы из попапа по платежам переходить на список платежей
          if (role === 'admin' || role === 'operator') {
            navigate(msg.to);
          } else {
            navigate(msg.to);
          }
        }}
        className="flex w-full items-center justify-between gap-3 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-indigo-700"
      >
        <span className="text-left leading-snug">{msg.text}</span>
        <span className="flex h-7 min-w-[2.5rem] items-center justify-center rounded-full bg-white/20 px-2 text-xs uppercase tracking-wide">
          Перейти
        </span>
      </button>
    </div>
  );
};

export default App;
